import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import { createListRow } from '../ui/panel';
import type { Campaign, Verdict } from '@core/campaign/campaign';
import { MYSTERY_IDS, type MysteryId } from '@core/mystery/types';
import { clearSave } from '../saveGame';
import { addBackdrop, FINALE_BACKDROP } from '../assets';

/**
 * Der Abschlussbericht.
 *
 * Der Spieler vertritt eine These und erfaehrt danach, was tatsaechlich dahinter
 * stand. Beides wird getrennt bewertet: ob er recht hatte, und ob seine eigenen
 * Funde die These trugen. Eine gut belegte, aber falsche Schlussfolgerung ist
 * der interessanteste Ausgang — und ausdruecklich vorgesehen.
 */
export class FinaleScene extends Phaser.Scene {
  private campaign!: Campaign;
  private choice: MysteryId | null = null;
  private verdict: Verdict | null = null;

  constructor() {
    super('Finale');
  }

  init(): void {
    this.choice = null;
    this.verdict = null;
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    this.redraw();
    this.cameras.main.fadeIn(400, 5, 8, 13);
  }

  private redraw(): void {
    this.children.removeAll();

    // Morgengrauen ueber offener See. Es ist das Letzte, was man von diesem
    // Fall sieht — und das einzige Bild im Spiel, das nicht duester ist.
    addBackdrop(this, FINALE_BACKDROP, 0.2);

    const { width, height } = this.scale;
    const centreX = width / 2;
    const panelWidth = Math.min(600, width - 80);
    const left = centreX - panelWidth / 2;

    this.add.text(centreX, 44, t('finale.title'), titleStyle(24)).setOrigin(0.5);

    if (this.verdict === null) {
      this.renderChoice(centreX, left, panelWidth, height);
    } else {
      this.renderVerdict(centreX, left, panelWidth, height, this.verdict);
    }
  }

  private renderChoice(centreX: number, left: number, panelWidth: number, height: number): void {
    this.add
      .text(centreX, 80, t('finale.prompt'), {
        ...readoutStyle(13),
        wordWrap: { width: panelWidth },
        align: 'center',
      })
      .setOrigin(0.5, 0);

    this.add
      .text(
        centreX,
        114,
        t('finale.collected', {
          collected: this.campaign.ledger.size,
          total: this.campaign.cluesPossible,
        }),
        readoutStyle(11, true),
      )
      .setOrigin(0.5);

    // Bewusst ohne Fundzahlen je Auflösung: Das wäre dieselbe Wertung, die
    // aus der Beweislage entfernt wurde, nur durch die Hintertür. Die Anklage
    // soll eine Behauptung sein, kein Ablesen.
    MYSTERY_IDS.forEach((id, index) => {
      createListRow(this, left, 146 + index * 50, {
        width: panelWidth,
        height: 44,
        title: t(`mystery.${id}`),
        detail: t(`mystery.${id}.tagline`),
        selected: this.choice === id,
        onClick: () => {
          this.choice = id;
          this.redraw();
        },
      });
    });

    if (this.choice !== null) {
      createButton(this, centreX, height - 56, t('finale.submit'), () => {
        this.verdict = this.campaign.accuse(this.choice as MysteryId);
        this.redraw();
      }, { width: 240 });
    }
  }

  /**
   * Der Abschluss.
   *
   * Nicht nur „richtig" oder „falsch": Erst kommt die Gegenrede oder die
   * Zustimmung aus der eigenen Crew, dann die Wahrheit im Wortlaut, dann die
   * Bilanz der Menschen — und zuletzt die Antwort auf die Frage, wegen der
   * die Figur ueberhaupt hier draussen war.
   */
  private renderVerdict(
    centreX: number,
    left: number,
    panelWidth: number,
    height: number,
    verdict: Verdict,
  ): void {
    const crewName = this.campaign.character.name;

    // Die Crew reagiert, bevor die Auflösung fällt.
    this.add
      .text(
        centreX,
        84,
        verdict.supported
          ? t('finale.crew.agree', { name: t('crew.first_officer') })
          : t('finale.crew.doubt', { name: t('crew.first_officer') }),
        {
          ...readoutStyle(12),
          color: verdict.supported ? PALETTE.inkDim : '#e0b070',
          wordWrap: { width: panelWidth },
          align: 'center',
        },
      )
      .setOrigin(0.5, 0);

    this.add
      .text(centreX, 136, verdict.correct ? t('finale.correct') : t('finale.wrong'), {
        fontFamily: FONT.display,
        fontSize: px(30),
        color: verdict.correct ? '#8fd9c4' : '#c9736f',
      })
      .setOrigin(0.5);

    this.add
      .text(centreX, 172, t('finale.truth', { truth: t(`mystery.${verdict.truth}`) }), readoutStyle(14))
      .setOrigin(0.5);

    // Die Auflösung im Wortlaut — der eigentliche Schluss der Geschichte.
    this.add
      .text(left, 202, t(`mystery.${verdict.truth}.revelation`), {
        ...readoutStyle(13),
        wordWrap: { width: panelWidth },
        lineSpacing: 4,
      })
      .setOrigin(0, 0);

    // Die Rechnung: Der Fall ließ sich nur lösen, indem Notrufe verstrichen.
    this.add
      .text(
        centreX,
        height - 186,
        t('finale.tally', { rescued: verdict.rescued, lost: verdict.lost }),
        { ...readoutStyle(13), color: verdict.lost > verdict.rescued ? '#c9736f' : PALETTE.ink },
      )
      .setOrigin(0.5);

    // Und die persönliche Antwort.
    this.add
      .text(
        centreX,
        height - 156,
        verdict.personalAnswered
          ? t('finale.personal.found', { name: this.campaign.character.lostName })
          : t('finale.personal.open', { name: this.campaign.character.lostName }),
        {
          ...readoutStyle(12),
          color: PALETTE.inkDim,
          fontStyle: 'italic',
          wordWrap: { width: panelWidth },
          align: 'center',
        },
      )
      .setOrigin(0.5, 0);

    this.add
      .text(
        centreX,
        height - 108,
        `${crewName}  ·  ${t('finale.collected', {
          collected: verdict.cluesCollected,
          total: verdict.cluesPossible,
        })}  ·  ${t('common.seed', { seed: this.campaign.seed })}`,
        { fontFamily: FONT.mono, fontSize: px(10), color: PALETTE.inkFaint },
      )
      .setOrigin(0.5);

    createButton(this, centreX, height - 56, t('finale.restart'), () => {
      // Der Durchlauf ist zu Ende — der Spielstand darf nicht bestehen bleiben.
      clearSave();
      this.registry.remove('campaign');
      this.scene.start('Menu');
    }, { width: 220 });
  }
}
